/** AuthProvider — drží session ze Supabase a vystavuje partner kontext.
 *  ProtectedRoute volá useAuth() a přesměruje na /login pokud session chybí.
 *
 *  Dvě cesty k partnerovi:
 *   1. partner_users mapping (skutečný partner) → partnerUser (role admin/member)
 *   2. admin impersonace: interní m-CRM admin bez mappingu — RPC
 *      current_partner_id() vrátí partnera z partner_impersonations
 *      → impersonated=true, partnerUser=null (read-only náhled). */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

interface PartnerProfile {
  partner_id: string;
  company_name: string;
  contact_name: string;
  email: string;
  city: string;
  billing_entity_id: string | null;
}

interface PartnerUserSelf {
  partner_user_id: string;
  role: "admin" | "member";
}

interface AuthState {
  loading: boolean;
  session: Session | null;
  user: User | null;
  partner: PartnerProfile | null;
  partnerUser: PartnerUserSelf | null;
  impersonated: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

async function loadPartnerProfile(partnerId: string): Promise<PartnerProfile | null> {
  const { data: partner } = await supabase
    .from("partners")
    .select("id, person_id")
    .eq("id", partnerId)
    .maybeSingle();
  if (!partner) return null;

  const { data: person } = await supabase
    .from("persons")
    .select("first_name, last_name, company_name, email, address_city")
    .eq("id", partner.person_id)
    .maybeSingle();

  return {
    partner_id: partner.id,
    company_name: person?.company_name || "",
    contact_name: [person?.first_name, person?.last_name].filter(Boolean).join(" ") || "",
    email: person?.email || "",
    city: person?.address_city || "",
    billing_entity_id: null,
  };
}

interface PartnerContextResult {
  partner: PartnerProfile | null;
  partnerUser: PartnerUserSelf | null;
  impersonated: boolean;
}

async function fetchPartnerContext(userId: string): Promise<PartnerContextResult> {
  // Čí data uvidíme, rozhoduje VÝHRADNĚ server: current_partner_id() zohledňuje
  // i aktivní admin náhled, který má přednost před vlastním mappingem.
  //
  // Dřív se tu nejdřív hledal vlastní partner_users mapping — jenže admin, který
  // má i vlastní portálový přístup, tím dostal svého partnera, zatímco RLS mu
  // vracela data náhledového. Načtení partnera pak neprošlo a portál hlásil,
  // že přístup není aktivní.
  const { data: activeId } = await supabase.rpc("current_partner_id");
  if (typeof activeId !== "string" || !activeId) {
    return { partner: null, partnerUser: null, impersonated: false };
  }

  const partner = await loadPartnerProfile(activeId);

  // Vlastní mapping čteme jen kvůli roli (správa uživatelů). Pokud ukazuje na
  // jiného partnera než ten aktivní, jsme v náhledu.
  const { data: pu } = await supabase
    .from("partner_users")
    .select("id, partner_id, role")
    .eq("auth_user_id", userId)
    .is("revoked_at", null)
    .maybeSingle();

  const ownAccess = pu && pu.partner_id === activeId;
  return {
    partner,
    partnerUser: ownAccess
      ? { partner_user_id: pu.id, role: (pu.role as "admin" | "member") || "member" }
      : null,
    impersonated: !ownAccess && partner !== null,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [partner, setPartner] = useState<PartnerProfile | null>(null);
  const [partnerUser, setPartnerUser] = useState<PartnerUserSelf | null>(null);
  const [impersonated, setImpersonated] = useState(false);
  const [loading, setLoading] = useState(true);
  // Dokud supabase-js nedokončí inicializaci, NESMÍME prohlásit uživatele za
  // nepřihlášeného. Přihlášení odkazem (magic link) přináší token ve fragmentu
  // URL a klient ho zpracovává asynchronně — kdyby mezitím ProtectedRoute
  // stihl přesměrovat na /login, router fragment zahodí a token je nenávratně
  // pryč. Přesně tím dosud padalo přihlášení odkazem z pozvánky i admin náhled.
  const [initialized, setInitialized] = useState(false);
  const touchedForUser = useRef<string | null>(null);

  useEffect(() => {
    let mounted = true;

    // getSession() čeká na initializePromise, jejíž součástí je i zpracování
    // tokenu z URL — po jeho resolve je stav auth klienta definitivní.
    void supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        setSession(data.session);
      })
      .finally(() => {
        if (mounted) setInitialized(true);
      });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
    });

    // Refresh session when window regains focus (long-running tab)
    const onFocus = () => {
      void supabase.auth.getSession().then(({ data }) => {
        if (!mounted) return;
        setSession(data.session);
      });
    };
    window.addEventListener("focus", onFocus);

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Klíčem je user.id, ne objekt session.user — ten je při každém
  // TOKEN_REFRESHED (~55 min) nová reference a effect by zbytečně
  // shodil celý strom pod ProtectedRoute (rozdělaný formulář, modal).
  const userId = session?.user?.id;

  useEffect(() => {
    let cancelled = false;
    if (!initialized) return; // ještě nevíme, jestli v URL nepřišel token
    if (!userId) {
      setPartner(null);
      setPartnerUser(null);
      setImpersonated(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    void fetchPartnerContext(userId).then((ctx) => {
      if (cancelled) return;
      setPartner(ctx.partner);
      setPartnerUser(ctx.partnerUser);
      setImpersonated(ctx.impersonated);
      setLoading(false);

      // Zapiš first/last_login_at (jen skutečný partner, 1× za session usera)
      if (ctx.partnerUser && touchedForUser.current !== userId) {
        touchedForUser.current = userId;
        void supabase.rpc("touch_partner_login");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [userId, initialized]);

  const value = useMemo<AuthState>(
    () => ({
      loading,
      session,
      user: session?.user ?? null,
      partner,
      partnerUser,
      impersonated,
      signOut: async () => {
        // Jen tento prohlížeč: výchozí scope „global" smaže všechny session uživatele, takže
        // konec impersonace by odhlásil i partnera na jeho počítači (Edge pak vrací 401).
        await supabase.auth.signOut({ scope: "local" });
      },
    }),
    [loading, session, partner, partnerUser, impersonated],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
